/* 블로그로 퍼가기 — /agents/portfolio/sns (스튜디오 홈 → 사진정리하기 → 블로그로 퍼가기)
   나의 시공사례(AI 시공사례)마다 · 제목 복사(네이버 제목 칸용)
                              · 네이버 블로그용 복사(요약 글 → 공사 개요표 → 페이지마다 글 + 사진 배치 표 → #해시태그, model/blog.ts)
                              · 사진 ZIP 받기(페이지 사진 + 시공 사진 원본, PNG) */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, EmptyState, ErrorState, Page, PageHeader, Skeleton, Temp_TileButton } from '@indipia/ui-core';
import { errorMessage, showcaseApi, type ShowcaseListItem } from './api/showcaseApi';
import { Toast } from './components/Common';
import { PageThumb } from './components/render';
import { CARD, CARD_GRID, CARD_THUMB, cx, relTime } from './components/ui';
import { loadShowcaseFonts } from './model/constants';
import { buildBlogContent, buildPhotoZip, saveFile, writeBlogClipboard } from './model/share';
import { toast } from './store/editor';

const caseUrl = (id: number) => `/agents/portfolio/showcase/${id}`;

export function BlogSharePage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<ShowcaseListItem[] | null>(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  /** 지금 작업 중인 시공사례와 작업 — 한 번에 하나씩 */
  const [busy, setBusy] = useState<{ id: number; what: 'blog' | 'zip' } | null>(null);

  useEffect(() => { loadShowcaseFonts(); }, []);
  useEffect(() => {
    let alive = true;
    setError(false);
    showcaseApi.list().then((l) => alive && setCases(l)).catch(() => { if (alive) { setCases([]); setError(true); } });
    return () => { alive = false; };
  }, [reload]);

  /* 블로그용 복사 — 클릭한 순간 클립보드 쓰기를 시작해야 브라우저가 허락해서, 내용은 다 만들어지면 채워지는 Promise 로 넘긴다.
     비공개면 "공개하고 복사" — 네이버가 사진을 가져가려면 공개 주소여야 한다 */
  const copyBlog = (c: ShowcaseListItem) => {
    if (busy) return;
    setBusy({ id: c.projectId, what: 'blog' });
    const job = (c.publicYn === 'Y' ? Promise.resolve() : showcaseApi.setPublic(c.projectId, true).then(() => { toast('공개로 바꿨어요 — 링크가 있으면 누구나 볼 수 있어요'); }))
      .then(() => showcaseApi.get(c.projectId))
      .then((p) => buildBlogContent({ projectId: p.projectId, title: p.title, pages: p.content.pages || [] }, toast));
    writeBlogClipboard(job)
      .then(() => toast('블로그용으로 복사했어요 — 네이버 블로그 글쓰기에서 Ctrl+V 로 붙여넣으세요'))
      .catch((err) => toast('블로그용으로 복사하지 못했어요: ' + errorMessage(err)))
      .finally(() => { setBusy(null); if (c.publicYn !== 'Y') setReload((n) => n + 1); });
  };
  const copyTitle = (c: ShowcaseListItem) => {
    navigator.clipboard.writeText(c.title.trim())
      .then(() => toast('제목을 복사했어요 — 네이버 블로그 글쓰기 제목 칸에 Ctrl+V 로 붙여넣으세요'))
      .catch((err) => toast('제목을 복사하지 못했어요: ' + errorMessage(err)));
  };
  const downloadZip = async (c: ShowcaseListItem) => {
    if (busy) return;
    setBusy({ id: c.projectId, what: 'zip' });
    try {
      const p = await showcaseApi.get(c.projectId);
      const z = await buildPhotoZip({ projectId: p.projectId, title: p.title, pages: p.content.pages || [] }, toast);
      saveFile(z.blob, z.name);
      toast(`저장했어요 — ${z.name}`);
    } catch (err) {
      toast('사진을 받지 못했어요: ' + errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  let body;
  if (error) body = <ErrorState title="나의 시공사례를 불러오지 못했어요" onRetry={() => setReload((n) => n + 1)} />;
  else if (!cases) {
    body = <div className={CARD_GRID} aria-busy="true">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="block" className="aspect-[3/4] w-full" />)}</div>;
  } else if (!cases.length) {
    body = (
      <EmptyState title="아직 만든 시공사례가 없어요" description="템플릿을 고르고 현장 사진을 고르면 AI가 시공사례를 만들어 드려요. 만든 시공사례는 여기서 블로그로 퍼갈 수 있어요."
        action={<Button onClick={() => navigate('/agents/portfolio/write')}>시공사례 만들기</Button>} />
    );
  } else {
    body = (
      <div className={CARD_GRID}>
        {cases.map((c) => {
          const pub = c.publicYn === 'Y', mine = busy?.id === c.projectId;
          return (
            <div key={c.projectId} className={CARD}>
              <Temp_TileButton plain className="rounded-lg text-left" aria-label={`${c.title} 편집하기`} title="눌러서 편집하기" onClick={() => navigate(caseUrl(c.projectId))}>
                <span className={cx(CARD_THUMB, 'block')}><PageThumb page={c.cover} width="fill" /></span>
              </Temp_TileButton>
              <b className="mx-01 mt-02 truncate text-body-sm font-semibold" title={c.title}>{c.title}</b>
              <span className="mx-01 flex items-center gap-xs text-caption text-text-secondary">
                <Badge status={pub ? 'success' : 'mute'}>{pub ? '공개' : '비공개'}</Badge>
                {c.pageCount}페이지 · {relTime(c.updatedAt)} 수정
              </span>
              <div className="flex flex-col gap-xs">
                <Button size="sm" disabled={!!busy} title={pub ? undefined : '네이버가 사진을 가져가려면 공개 주소가 필요해서, 공개로 바꾼 뒤 복사해요'}
                  onClick={() => copyBlog(c)}>{mine && busy?.what === 'blog' ? '복사하는 중…' : pub ? '네이버 블로그용 복사' : '공개하고 블로그용 복사'}</Button>
                <Button size="sm" variant="etc" onClick={() => copyTitle(c)}>제목 복사</Button>
                <Button size="sm" variant="etc" disabled={!!busy} onClick={() => void downloadZip(c)}>
                  {mine && busy?.what === 'zip' ? '사진 만드는 중…' : '사진 ZIP 받기'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div data-showcase className="h-full text-text">
      <Page layout="doc" width="full">
        <PageHeader title="블로그로 퍼가기"
          subtitle="만든 시공사례를 네이버 블로그에 붙여넣거나 사진으로 받아요. 블로그용 복사는 글은 검색되는 글자로, 사진은 템플릿 배치 그대로 표로 넣고 끝에 #해시태그(태그 칸에 자동 등록)를 붙여요. 제목은 [제목 복사]로 제목 칸에 넣어 주세요." />
        {body}
      </Page>
      <Toast />
    </div>
  );
}
